import { SET_AUTO_APPLIED_VOUCHER } from 'actions/actionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { isMetasearchDiscountEnabled } from 'lib/metasearch/metasearchDiscountContext'
import { createMetasearchDiscountVoucher } from 'lib/metasearch/metasearchVoucherApi'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { getSourceForProperty } from 'selectors/offerSelectors'
import { SOURCE_TYPES } from 'constants/source'

const SUPPORTED_GHA_VOUCHER_OCCUPANCIES = new Set(['1', '2'])

export interface CreateGhaVoucherInput {
  accountType?: string
  /** Absent when Google's click carried no RATE-PLAN-ID; `rateRuleId` then carries the claim. */
  token?: string
  propertyId?: string
  checkIn?: string
  checkOut?: string
  rooms?: Array<App.Occupants>
  mx?: string
  rateRuleId?: string
  /**
   * Required when dispatching from anywhere but the offer page: the fallback below derives
   * the source from the current route, which decides the voucher's CUG entitlement.
   */
  source?: string
  /** The bedbank enquiry being bought; svc-connection-google prices the voucher off it. */
  bedbankSessionId?: string
  /** Google's displayed total and its currency, from the landing URL (GHA-585). */
  advertisedPrice?: number
  advertisedCurrency?: string
}

function isSupportedGhaVoucherOccupancy(occupancy: Array<string>): boolean {
  return occupancy.length === 1 && SUPPORTED_GHA_VOUCHER_OCCUPANCIES.has(occupancy[0])
}

/** Resolves true when a voucher was minted — the guest discount preview stands down on false. */
export function createGhaVoucher(input: CreateGhaVoucherInput): AppAction {
  return async(dispatch, getState): Promise<boolean> => {
    // GHA-585: a bare rate rule ID is enough — the service re-selects the property's current
    // discount for the leg it names. Only a click carrying neither identifier has nothing to mint
    // from.
    if (
      !isMetasearchDiscountEnabled() ||
      (!input.token && !input.rateRuleId) ||
      !input.propertyId ||
      !input.checkIn ||
      !input.checkOut ||
      !input.rooms?.length
    ) {
      return false
    }

    const occupancy = serialiseOccupancy(input.rooms)
    if (!isSupportedGhaVoucherOccupancy(occupancy)) {
      return false
    }

    const state = getState()
    const source = input.source ?? getSourceForProperty(state, input.propertyId)
    const response = await createMetasearchDiscountVoucher({
      token: input.token,
      accountType: input.accountType,
      propertyId: input.propertyId,
      checkIn: input.checkIn,
      checkOut: input.checkOut,
      occupancy,
      region: state.geo.currentRegionCode,
      isCugRate: source.includes(SOURCE_TYPES.CUG_METASEARCH),
      mx: input.mx,
      rateRuleId: input.rateRuleId,
      bedbankSessionId: input.bedbankSessionId,
      advertisedPrice: input.advertisedPrice,
      advertisedCurrency: input.advertisedCurrency,
    })

    if (response.voucherId) {
      dispatch({
        type: SET_AUTO_APPLIED_VOUCHER,
        data: response.voucherId,
      })
      return true
    }
    return false
  }
}
